import { useEffect } from 'react'
import { Box, List } from '@mui/material'
import { i18nTranslationHelper } from '@/i18n'
import { useFeatureFlags, FeatureFlagName } from '@/infra/feature-flag'
import SearchField from '@/presentation/components/ui/input/search-field'
import SearchAutocomplete from './search-autocomplete'
import SearchTypeDecisor from './search-type-decisor'
import { MIN_AUTOCOMPLETE_SEARCH_LENGTH, useGlobalSearch } from './use-global-search'
import CareerMentorSearchBarCard from '../../career-mentor/search-bar-card'

const GlobalSearchField = () => {
  const t = i18nTranslationHelper.getTComponents
  const { careerMentorEnabled } = useFeatureFlags({
    careerMentorEnabled: FeatureFlagName.ENABLE_CAREER_MENTOR
  })
  const {
    search,
    searchType,
    displayAutocomplete,
    loadingAutocomplete,
    matchedWords,
    handleSearch,
    handleAutoCompleteSelect,
    handleSearchTypeChange,
    handleSearchTypeUpdate,
    handleSearchInputFocus,
    handleClearInput,
    autoComplete,
    setDisplayAutocomplete
  } = useGlobalSearch()

  const handleClickOutside = (event: MouseEvent) => {
    const searchFieldElement = document.getElementById('global-search-field')
    const autocompleteElement = document.getElementById('autocomplete')
    const dialogElement = document.querySelector('[role="dialog"]')

    if (dialogElement?.contains(event.target as Node)) {
      return
    }

    if (
      autocompleteElement &&
      !autocompleteElement.contains(event.target as Node) &&
      searchFieldElement &&
      !searchFieldElement.contains(event.target as Node)
    ) {
      setDisplayAutocomplete(false)
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <Box
      sx={{
        position: 'relative'
      }}
    >
      <SearchField
        id="global-search-field"
        placeholder={t('bannerSearch.placeholder')}
        onSearch={value => handleSearch(value, searchType)}
        onFieldChange={autoComplete}
        onFocusChange={handleSearchInputFocus}
        onClearInput={handleClearInput}
        value={search}
      />

      <Box
        id="autocomplete"
        role="listbox"
        aria-label={t('bannerSearch.searchSuggestions')}
        aria-live="polite"
        aria-atomic="true"
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          borderRadius: `8px`,
          background: `#FFF`,
          marginTop: '0.25rem',
          border: `1px solid #dddddd`,
          padding: '0.25rem',
          visibility: displayAutocomplete ? 'visible' : 'hidden',
          opacity: displayAutocomplete ? 1 : 0,
          transition: 'visibility 0.1s ease-in-out, opacity 0.1s ease-in-out'
        }}
      >
        {displayAutocomplete && matchedWords && matchedWords.length > 0 && (
          <Box className="sr-only" aria-live="polite">
            {matchedWords.length}{' '}
            {matchedWords.length === 1
              ? t('accessibility.searchResultsSingular')
              : t('accessibility.searchResultsPlural')}
          </Box>
        )}
        <List dense role="presentation">
          <SearchTypeDecisor
            onSearchTypeChange={handleSearchTypeChange}
            onSearchTypeUpdate={handleSearchTypeUpdate}
          />
          <SearchAutocomplete
            loading={loadingAutocomplete}
            matchedWords={matchedWords}
            onSelect={handleAutoCompleteSelect}
            searchTerm={search}
          />
        </List>

        {careerMentorEnabled &&
          displayAutocomplete &&
          search.length >= MIN_AUTOCOMPLETE_SEARCH_LENGTH && <CareerMentorSearchBarCard />}
      </Box>
    </Box>
  )
}

export default GlobalSearchField
